<template>
  <div ref="scrollContainerEl" class="clinic-inside">
    <figure ref="canvasContainerEl" class="scene-3d" id="clinic-3d-scene">
      <Gradient />
      <canvas ref="canvasEl" class="canvas"> </canvas>
      <ClinicControls
        v-if="controlsEnabled && scrollContainerEl && started"
        :scrollContainerEl="scrollContainerEl"
        :progress="progress"
        :isPlaying="isPlaying"
      />
      <div class="texts _hidden-vl">
        <div
          v-for="(contentScene, index) in contentScenes"
          :key="contentScene.id"
          class="room"
          :class="[`-room-${index + 1}`]"
        >
          <div class="card -title -content">
            <h3 class="title text-lg">{{ contentScene.title }}</h3>
          </div>
          <div class="card -copy">
            <p class="text text-2xs">
              {{ contentScene.copy }}
            </p>
          </div>
          <div
            class="card -video"
            :class="{ '-playing': isPlaying, '-empty': !contentScene.video }"
          >
            <figure
              v-if="contentScene.video && contentScene.video.video"
              class="video-wrapper"
            >
              <div class="playbutton" @click="playVideo">
                <img :src="'/icons/play-button.png'" />
              </div>
              <VideoJSPlayer
                :options="{
                  controls: false,
                  muted: false,
                  auto: true,
                  fill: true,
                  disablePictureInPicture: true,
                  sources: [
                    {
                      src: contentScene.video.video.streamingUrl,
                      type: 'application/x-mpegURL',
                    },
                  ],
                }"
                :poster="posterImage(contentScene) || undefined"
                @play="onPlay"
                @pause="onPause"
                @ended="onEnded"
                @timeupdate="onTimeUpdate"
              />
            </figure>
            <div class="progress">
              <div class="bar"></div>
            </div>
          </div>
        </div>
      </div>
      <div class="overlay-background" :class="{ '-active': isPlaying }">
        <div class="blobs">
          <img src="/icons/indicator-blobs.svg" alt="indicator blobs" />
        </div>
      </div>
      <ReturnButton
        class="returnbutton _hidden-vl"
        :is-visible="isPlaying"
        :on-click="onEnded"
      />
    </figure>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, shallowRef } from 'vue';
import gsap from 'gsap';
import { useIntersectionObserver } from '@vueuse/core';
import Gradient from '~/components/partials/Gradient.vue';
import VideoJSPlayer from '~/components/partials/VideoJSPlayer.vue';
import ReturnButton from '~/components/partials/ReturnButton.vue';
import ClinicControls from '~/components/clinic-3d/ClinicControls.vue';
import {
  type Clinic3dOptions,
  type POCVersion,
  useClinic3d,
} from '~/clinic-inside/useClinic3d';
import type { ContentSceneFragment } from '#gql';
import { getMuxThumbnail } from '~/utils/GetMuxThumbnail';

const { localeProperties } = useI18n();

const props = defineProps<{
  version: POCVersion;
  started: boolean;
  initTimeout?: number;
  controlsEnabled?: boolean;
  options?: Clinic3dOptions;
  initCallback?: () => void;
}>();

const scrollContainerEl = ref<null | HTMLElement>(null);
const canvasContainerEl = ref<null | HTMLElement>(null);
const canvasEl = ref<null | HTMLCanvasElement>(null);

const isPlaying = ref(false);
const activeVideo = ref<null | HTMLVideoElement>(null);

// used to start/stop tick request for threejs
const tick: {
  start: null | (() => void);
  stop: null | (() => void);
} = {
  start: null,
  stop: null,
};

const { data } = useAsyncGql({
  operation: 'GetClinicExperience',
  variables: { locale: localeProperties.value.siteLocale },
});

const { version, controlsEnabled, options, initCallback } = props;

const contentScenes = computed(function () {
  return data.value?.clinicExperience?.contentScenes ?? [];
});

const posterImage = (scene: ContentSceneFragment) => {
  if (scene.thumbnail?.responsiveImage?.src) {
    return scene.thumbnail?.responsiveImage?.src;
  } else if (scene.video?.video?.muxPlaybackId) {
    return getMuxThumbnail(scene.video?.video?.muxPlaybackId);
  }
  return undefined;
};

watch(
  () => props.started,
  function () {
    if (props.started && tick.start) {
      tick.start();
      document.documentElement.setAttribute('data-experience', 'ready');
    }
  },
);

const { progress } = useClinic3dProgress();

const targetIsVisible = shallowRef(false);
const hasInitialized = ref(false);

const { stop: stopIntersectionObserver } = useIntersectionObserver(
  scrollContainerEl,
  ([entry]) => {
    targetIsVisible.value = entry?.isIntersecting || false;
  },
  { rootMargin: '50%' },
);

watch(targetIsVisible, (value) => {
  if (value && !hasInitialized.value) {
    hasInitialized.value = true;
    // initExperience();
  }
});

onMounted(() => {
  initExperience();
});

onBeforeUnmount(() => {
  stopIntersectionObserver();

  if (tick.stop) {
    tick.stop();

    document.documentElement.setAttribute('data-experience', 'stopped');
  }
});

async function initExperience() {
  if (typeof document !== 'undefined') {
    document.addEventListener(VideoPlayer.eventVideoStart, onVideoStart);
    document.addEventListener(VideoPlayer.eventVideoClose, onVideoClose);
  }

  while (
    !canvasContainerEl.value ||
    !canvasEl.value ||
    !scrollContainerEl.value ||
    contentScenes.value.length === 0
  ) {
    await new Promise((resolve) => setTimeout(resolve, 100)); // Small delay to avoid infinite blocking
  }

  if (canvasContainerEl.value && canvasEl.value && scrollContainerEl.value) {
    const { start, stop } = await useClinic3d(
      canvasContainerEl.value,
      canvasEl.value,
      scrollContainerEl.value,
      version,
      contentScenes.value,
      options,
    );

    tick.start = start;
    tick.stop = stop;
    initCallback?.();
  }
}

function playVideo(event: Event) {
  const button = event?.target as HTMLElement;
  const video = button.closest('.video-wrapper')?.querySelector('video');
  video?.play();
}

function onPlay() {
  useScrollLock(true);
  activeVideo.value = event?.target as HTMLVideoElement;
  isPlaying.value = true;
  gsap.to('.progress .bar', {
    width: '0%',
  });
}

function onPause() {
  useScrollLock(false);
  isPlaying.value = false;
}

function onEnded() {
  activeVideo.value?.pause();
  useScrollLock(false);
  isPlaying.value = false;
}

function onVideoStart() {
  isPlaying.value = true;
}

function onVideoClose() {
  isPlaying.value = false;
}

function onTimeUpdate(timePercentage: number) {
  gsap.set('.progress .bar', {
    width: `${timePercentage * 100}%`,
  });
}
</script>

<style lang="scss">
html:not([data-experience='running']) {
  .scene-3d > .texts {
    display: none;
  }
}
</style>

<style scoped lang="scss">
* {
  position: relative;
}

.scene-3d {
  height: 100vh;
  position: relative;
  overflow: hidden;

  > .canvas {
    position: relative;
    width: 100% !important;
    height: 100%;
    z-index: 10;
  }

  > .controls {
    position: absolute;
    inset: auto 1rem 1rem auto;
  }

  > .texts {
    width: 100%;
    height: 100%;
    position: fixed;
    inset: 0 0 auto 0;
    z-index: 20;
    pointer-events: none;

    > .room {
      position: absolute;
      display: flex;
      left: 0;
      width: 100%;
      height: 100%;
      justify-content: center;
      color: white;

      > .card {
        position: absolute;
        bottom: 5rem;
        border-radius: 0.5rem;

        padding: 0 2rem;
        width: 100%;
        pointer-events: all;

        &.-empty {
          visibility: hidden;
        }
      }
      .card.-video {
        aspect-ratio: 16 / 9;
        width: 70%;
        padding: 0;

        &.-playing {
          position: fixed;
          bottom: calc(50% - 5rem);
          width: calc(100% - 1.5rem);

          .playbutton {
            opacity: 0;
          }
          .progress {
            opacity: 1;
          }
        }

        .playbutton {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          img {
            width: calc(2.5rem + 8px);
            height: calc(2.5rem + 8px);
          }
        }

        .video-wrapper {
          border-radius: 0.5rem;
          overflow: clip;
          height: 100%;
          width: 100%;
          div {
            height: 100%;
            width: 100%;
          }
        }
        .progress {
          position: absolute;
          top: calc(100% + 2.875rem);
          width: 100%;
          height: 4px;
          background: var(--base-color-white-40);
          display: flex;
          opacity: 0;
          border-radius: 2px;
          overflow: hidden;
          .bar {
            width: 0%;
            height: 4px;
            background: var(--color-white);
          }
        }
      }
    }

    > :deep(.text),
    > :deep(.title) {
      position: absolute;
      inset: auto auto 2.5rem 2.5rem;
      width: 50vw;
    }

    > :deep(.text) {
      @extend .text-md;

      transform-origin: center left;
      width: 50ch;
    }

    > :deep(.title) {
      @extend .text-2xl;

      text-align: center;
      transform-origin: center left;
      width: 30ch;
    }
  }
}

.overlay-background {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0);
  z-index: 10;
  transition: background 0.3s ease-in-out;
  &.-active {
    background: rgba(0, 0, 0, 0.6);

    .blobs {
      opacity: 1;
    }
  }
  .blobs {
    transition: opacity 0.3s ease-in-out;
    opacity: 0;
    position: absolute;
    bottom: -40%;
    left: -71%;
    right: 10%;
  }
}

.returnbutton {
  top: unset;
  right: unset;
  left: 1rem;
  bottom: 1rem;
}
</style>
